import type { TemplateResult } from 'lit';
import { html } from 'lit';
import { EntityContent } from 'src/content/entity-content.js';
import '../../library/fields/index.js';
import '../../library/editors/index.js';
import '../../library/lists/d-list-section.js';
import '../../library/lists/d-list-section-attachment.js';
import '../../library/elements/d-label.js';
import './d-edit-partner-access.js';
import { customElement } from 'lit/decorators.js';
import { isEmptyOrInvalidString, uuid } from 'src/utilities/text';
import { t } from 'src/i18n';
import type { UpdateSectionItem } from '../update-section-item';
import type { CreateEntityInput } from '../../layout/parts/create-entity-input';
import type { ListDefinition } from '../list-definition';
import type { PartnerView } from './partner-view';
import type { PartnerViewEditItem } from './partner-view-edit-item';
import type { SectionField } from '../../library/components/section-field';
import type { ActionInput } from '../../library/elements/action-input';

/**
 *
 * STATUS OK
 */
@customElement('d-partner-view')
export class DPartnerView extends EntityContent<PartnerView, PartnerViewEditItem> {
  renderViewMode() {
    return html` ${this.renderFieldSection('partners', this.viewFields(), this.entityView)} `;
  }

  async initializeEditItem() {
    this.editItem = {
      address: this.entityView.address,
      email: this.entityView.email,
      industry: this.entityView.industry,
      notes: this.entityView.notes,
      pages: this.entityView.pages,
      phone: this.entityView.phone,
      physicalAccess: this.entityView.physicalAccess,
      remoteAccess: this.entityView.remoteAccess,
      url: this.entityView.url,
      name: this.entityView.name,
    };
  }

  doneDisabled(): boolean {
    return isEmptyOrInvalidString(this.editItem?.name);
  }

  renderEditAccess(item: PartnerViewEditItem): TemplateResult<1> {
    return html`
      <d-edit-partner-access
        field="partners_access"
        class="fullWidth"
        ?physicalAccessValue=${item.physicalAccess}
        ?remoteAccessValue=${item.remoteAccess}
        @access-changed=${(e: CustomEvent<{ physical: boolean; remote: boolean }>) => {
          this.editItem = { ...item, physicalAccess: e.detail.physical, remoteAccess: e.detail.remote };
        }}
      >
      </d-edit-partner-access>
    `;
  }

  renderEditItem(item: PartnerViewEditItem): TemplateResult<1> {
    return html` ${this.renderFieldSection('partners', this.editFields(item), item)} `;
  }

  protected viewFields(): SectionField[] {
    const m = this.entityView.featureStates;
    return [
      {
        field: 'industry',
        type: 'view-text',
        class: 'minWidth300',
        condition: m.entityPartnersComplete,
      },
      {
        field: 'phone',
        type: 'view-text',
        class: 'minWidth300',
        condition: m.entityPartnersComplete,
      },
      {
        field: 'address',
        type: 'view-text',
        class: 'fullWidth',
        condition: m.entityPartnersComplete,
      },
      {
        field: 'email',
        type: 'view-text',
        class: 'minWidth300',
        condition: m.entityPartnersComplete,
      },
      {
        field: 'url',
        type: 'view-text',
        class: 'minWidth300',
        condition: m.entityPartnersComplete,
      },
      {
        field: 'access',
        type: 'view-conditional-bullet-list',
        condition: m.entityPartnersComplete,
        listItems: [
          {
            field: 'physicalAccess',
          },
          {
            field: 'remoteAccess',
          },
        ],
      },
      {
        field: 'notes',
        type: 'view-text',
        class: 'fullWidth',
        condition: m.entityPartnersComplete,
      },
    ];
  }

  protected lists(): ListDefinition[] {
    let contactActions: ActionInput[] = [];
    let contractActions: ActionInput[] = [];
    if (this.entityView.currentUserHasWriteAccess) {
      if (!this.emergencyContent || this.entityView.currentUserHasEmergencyAccess) {
        contactActions = [
          {
            name: t('employees.add'),
            action: 'createContact',
            slot: 'top-right',
          },
        ];
      }
      contractActions = [
        {
          name: t('employees.add'),
          action: 'createContract',
          slot: 'top-right',
        },
      ];
    }
    return [
      {
        field: 'organization_contacts',
        icon: 'employees',
        actions: contactActions,
        items: 'contacts',
      },
      {
        field: 'organization_contracts',
        icon: 'contracts',
        actions: contractActions,
        items: 'contracts',
        condition:
          this.entityView.featureStates.core || this.entityView.featureStates.data || this.entityView.featureStates.hse,
      },
      {
        field: 'generalFields_attachments',
        type: 'custom',
        icon: 'attachments',
        items: 'attachments',
        render: () => this.renderAttachments(),
      },
    ];
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return undefined;
  }

  protected createContact() {
    const id = uuid();
    const input: CreateEntityInput = {
      entityType: 'contacts',
      entityUuid: id,
      targetUrl: this.entityView.href + '/contacts/' + id + '?edit',
      partnerId: this.entityView.uuid,
    };
    this.dispatchEvent(
      new CustomEvent<CreateEntityInput>('create-entity', {
        bubbles: true,
        composed: true,
        detail: input,
      }),
    );
  }

  protected createContract() {
    const id = uuid();
    const input: CreateEntityInput = {
      entityType: 'contracts',
      entityUuid: id,
      targetUrl: this.entityView.href + '/contracts/' + id + '?edit',
      pageId: 63,
      partnerId: this.entityView.uuid,
    };
    this.dispatchEvent(
      new CustomEvent<CreateEntityInput>('create-entity', {
        bubbles: true,
        composed: true,
        detail: input,
      }),
    );
  }

  private editFields(item: PartnerViewEditItem): SectionField[] {
    const m = this.entityView.featureStates;
    return [
      {
        field: 'name',
        type: 'edit-text',
        class: 'minWidth300',
        autofocus: !this.entityView.isConfirmedEntity,
        selectOnFocus: !this.entityView.isConfirmedEntity,
        markIfEmpty: true,
        placeholder: t('reports.fieldMustBeFilledOut'),
      },
      {
        field: 'phone',
        type: 'edit-text',
        class: 'minWidth200',
        condition: m.staffing || m.core || m.data,
      },
      {
        field: 'industry',
        type: 'edit-text',
        class: 'minWidth200',
        condition: m.core || m.data || m.hse,
      },
      {
        field: 'address',
        type: 'edit-textarea',
        class: 'fullWidth',
        condition: m.core || m.data || m.hse,
      },
      {
        field: 'email',
        type: 'edit-text',
        class: 'minWidth200',
        condition: m.core || m.data || m.hse,
      },
      {
        field: 'url',
        type: 'edit-text',
        class: 'minWidth200',
        condition: m.core || m.data || m.hse,
      },
      {
        type: 'custom',
        render: () => this.renderEditAccess(item),
        condition: m.core || m.data || m.hse,
      },
      {
        field: 'notes',
        type: 'edit-textarea',
        class: 'fullWidth',
        condition: m.core || m.data || m.hse,
      },
    ];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-partner-view': DPartnerView;
  }
}
